import { withDatasourceCheck, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { useState, useRef, FormEvent, ElementRef } from 'react';
import StepProgress from 'components/Generic/Forms/SalesforceIntegratedForms/StepProgress';
import FieldTypeDefiner from 'components/Generic/Forms/SalesforceIntegratedForms/FieldTypeDefiner';
import Summary from 'components/Generic/Forms/SalesforceIntegratedForms/Summary';
import StepSelector from 'components/Generic/Forms/SalesforceIntegratedForms/StepSelector';
import { useFormContext } from 'components/Generic/Forms/SalesforceIntegratedForms/FormContext';
import { pushDataLayerEvent } from 'hooks/useDataLayer';
import {
  postSalesforceFormDataToEloqua,
  postSalesforceOnboardingFormData,
} from 'api/SalesforceOnboardingForm';
import { useRouter } from 'next/router';
import WithFormProvider from 'components/Generic/Forms/SalesforceIntegratedForms/WithFormProvider';
import { FieldsType } from 'model/SalesforceField';

type ElementItemsType = {
  displayName: string;
  fields: {
    items: [
      {
        id: string;
        template: string;
        displayName: string;
        fields: FieldsType;
      }
    ];
    step: {
      value: string;
    };
    title: Field<string>;
  };
};

type SalesforceIntegratedFormProps = ComponentProps & {
  fields: {
    recordTypeId: {
      value: string;
    };
    helpSection: {
      value: string;
    };
    items: ElementItemsType[];
    submissionType: {
      value: string;
    };
    submissionEndpoint: {
      value: string;
    };
    endPage: {
      value: {
        href: string;
      };
    };
  };
};

const isValidatable = (element: any): element is ElementInternals => {
  return 'willValidate' in element;
};
const SalesforceIntegratedForm = ({ fields }: SalesforceIntegratedFormProps): JSX.Element => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showError, setShowError] = useState(false);
  const formWrapper = useRef<ElementRef<'section'>>(null);
  const formRef = useRef<ElementRef<'form'>>(null);
  const { formData, updateFormData } = useFormContext();
  const router = useRouter();

  const currentStepData = fields.items.find(
    (step) => Number(step.fields.step.value) === currentStep
  )?.fields;
  const nextStepLabel: string = `Continue to '${fields.items[currentStep]?.displayName}'`;
  const prevStepLabel: string = `Back to '${fields.items[currentStep - 2]?.displayName}'`;
  const showSummary: boolean = currentStepData?.title.value === 'Summary';
  const getFormTypeSubmit: string = fields.submissionType.value;

  const scrollPage = (): void => {
    if (formWrapper.current) {
      formWrapper.current.scrollIntoView();
    }
  };

  const validateStepFields = () => {
    const form = formRef.current;
    let isValid = true;
    if (form) {
      for (const element of form.elements) {
        if (!isValidatable(element)) continue;
        if (element.willValidate) {
          if (!element.checkValidity()) {
            isValid = false;

            // Trigger the invalid event manually
            const invalidEvent = new Event('invalid', {
              bubbles: true,
              cancelable: true,
            });

            element.dispatchEvent(invalidEvent);
            element.scrollIntoView({ block: 'center' });
          }
        }
      }
    }

    return isValid;
  };

  const nextStepHandler = (): void => {
    if (validateStepFields()) {
      scrollPage();
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStepHandler = (): void => {
    scrollPage();
    setCurrentStep(currentStep - 1);
    setShowError(false);
  };

  const submitForm = (event: FormEvent) => {
    event.preventDefault();
    if (validateStepFields()) {
      pushDataLayerEvent({
        event: 'form_submit',
        formName: 'Form name',
        form_field: 'n/a',
      });
      // temporary solution to submit form data to Eloqua endpoint while Adviser team working with required changes
      if (getFormTypeSubmit === 'Eloqua') {
        const mappedValues = new FormData();
        for (const [key, value] of Object.entries(formData.records[0])) {
          mappedValues.append(key, value);
        }
        postSalesforceFormDataToEloqua(fields.submissionEndpoint?.value, mappedValues).then(
          (response) => {
            if (response.status === 200) {
              router.push(fields.endPage.value.href);
            } else {
              setShowError(true);
            }
          }
        );
      } else {
        updateFormData({
          fieldName: 'recordTypeId',
          value: fields.recordTypeId.value,
        });
        updateFormData({
          fieldName: 'attributes',
          value: { type: getFormTypeSubmit },
        });
        const updatedForm = { ...formData };
        updatedForm.records[0].attributes = { type: getFormTypeSubmit };
        updatedForm.records[0].recordTypeId = fields.recordTypeId.value;
        postSalesforceOnboardingFormData(updatedForm).then((response) => {
          if (response.status === 200 && response.data === true) {
            router.push(fields.endPage.value.href);
          } else {
            setShowError(true);
          }
        });
      }
    }
  };

  return (
    <section ref={formWrapper} className="default-section-margin">
      {!showError && (
        <div className="container">
          <StepProgress steps={fields.items} activeStep={currentStep} />
          <h2 className="my-5">
            {currentStepData?.title.value}
            <span className="subheader-1 ms-4 step-subtitle">
              Step {currentStep} of {fields.items.length}
            </span>
          </h2>
          <div className="row">
            {!showSummary && (
              <div className="col-8">
                <form ref={formRef}>
                  {currentStepData?.items.map((item) => (
                    <FieldTypeDefiner
                      key={item.id}
                      displaySection={true}
                      templateName={item.template}
                      sectionName={item.displayName}
                      fields={item.fields}
                      stepName={currentStepData.title.value}
                    />
                  ))}
                </form>
              </div>
            )}

            {showSummary && <Summary onStepChange={(step) => setCurrentStep(step)} />}

            <div
              className="col-4 help-section"
              dangerouslySetInnerHTML={{ __html: fields.helpSection.value }}
            />
          </div>
        </div>
      )}
      {showError && (
        <div className="container">
          <h2 className="mb-2">An error occurred during asset request submittion</h2>
          <p className="mb-4">
            Thank you for submitting your request form, however an internal error occurred during in
            the system. Please try again later.
          </p>
          <h3>Start another asset request</h3>
          <p className="mb-2">
            If you&apos;d like to start another asset request form please click the &apos;Start
            another request&apos; button below.
          </p>
        </div>
      )}
      <StepSelector
        currentStep={currentStep}
        prevStepHandler={prevStepHandler}
        nextStepHandler={nextStepHandler}
        showSubmit={currentStep === fields.items.length && !showError}
        showNextStepBtn={currentStep !== fields.items.length}
        nextStepLabel={nextStepLabel}
        prevStepLabel={prevStepLabel}
        submitForm={submitForm}
      />
    </section>
  );
};

export default withDatasourceCheck()(WithFormProvider(SalesforceIntegratedForm));
